import { useCallback, useMemo } from "react";
import { useProposalMutations } from "@stockline/web";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import type { ProductDefaults } from "../utils/resolveProductDefaults";
import { isProposalFlow } from "../types/proposal.types";
import { findPreOrderProposal } from "../utils/proposalContext";

interface UseProposalFlowHandlersOptions {
  preOrder: PreOrder;
}

interface FlowHandlers {
  handleFlowUpdate: (
    flowId: string,
    updates: Partial<PreOrderFlow>,
  ) => Promise<void>;
  handleFlowCreate: (defaults: ProductDefaults) => Promise<void>;
  handleFlowDelete: (flowId: string) => Promise<void>;
}

const NUMERIC_FLOW_FIELDS = new Set([
  "quantity",
  "weight",
  "price",
  "price_per_weight",
]);

function flowUpdatesToPayload(
  updates: Partial<PreOrderFlow>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) continue;
    payload[key] = value;
  }
  // Map legacy frontend field name to backend payload field
  if ("price_per_weight" in payload && !("price" in payload)) {
    payload.price = payload.price_per_weight;
    delete payload.price_per_weight;
  }
  return payload;
}

function defaultsToFlowPayload(
  defaults: ProductDefaults,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    product_id: defaults.product_id,
    quantity: 0,
  };
  if (defaults.unit_id) payload.unit_id = defaults.unit_id;
  if (defaults.weight !== undefined) payload.weight = defaults.weight ?? 0;
  if (defaults.price_per_weight !== undefined)
    payload.price = defaults.price_per_weight;
  return payload;
}

function findFlowSourceProposalId(
  preOrder: PreOrder,
  flowId: string,
): string | null {
  const flow = preOrder.flows?.find((f) => f.id === flowId);
  if (flow && isProposalFlow(flow)) {
    return flow.__proposalDiff?.sourceProposalId ?? null;
  }
  return null;
}

/**
 * Virtual flows produced by an update proposal's `lines_to_add` carry a
 * synthetic id of the form `proposal-flow-{proposalId}::add::{index}`.
 * This lets edits/removes target the specific entry in the lines_to_add
 * array rather than incorrectly routing to lines_to_change.
 */
function parseLinesToAddIndex(flowId: string): number | null {
  const match = /::add::(\d+)$/.exec(flowId);
  return match ? Number(match[1]) : null;
}

/**
 * Flow handlers that route mutations to the proposal payload instead of the
 * live entity. Used when rendering a pending proposal so the user can tweak
 * fields before approving.
 *
 * - Create-batch flows: each virtual flow has a 1-to-1 source Proposal record
 *   (`__proposalDiff.sourceProposalId`); edits/removes hit that Proposal.
 *   New flows go through `addFlowProposal` (creates a new Proposal in the batch).
 * - Update-batch flows: the parent pre_order Proposal carries
 *   `lines_to_change`/`lines_to_add`/`lines_to_delete` arrays. Edits patch
 *   the corresponding entry in those arrays via `editProposal`.
 */
export function useProposalFlowHandlers({
  preOrder,
}: UseProposalFlowHandlersOptions): FlowHandlers {
  const { editProposal, removeProposal, addFlowProposal } =
    useProposalMutations();

  const ctx = useMemo(() => findPreOrderProposal(preOrder), [preOrder]);
  const operation = ctx?.proposal.operation;

  const handleFlowUpdate = useCallback(
    async (flowId: string, updates: Partial<PreOrderFlow>): Promise<void> => {
      if (!ctx) return;
      const payload = flowUpdatesToPayload(updates);
      if (Object.keys(payload).length === 0) return;

      if (operation === "create") {
        const sourceProposalId = findFlowSourceProposalId(preOrder, flowId);
        if (!sourceProposalId) return;
        await editProposal(ctx.batchId, sourceProposalId, payload);
        return;
      }

      if (operation === "update") {
        // Virtual lines_to_add row: update its entry in lines_to_add by index
        // rather than appending a (mismatched id) entry to lines_to_change.
        const addIndex = parseLinesToAddIndex(flowId);
        if (addIndex !== null) {
          const existing = (ctx.proposal.payload?.lines_to_add ?? []) as Array<
            Record<string, unknown>
          >;
          if (addIndex >= existing.length) return;
          const next = existing.map((l, i) =>
            i === addIndex ? { ...l, ...payload } : l,
          );
          await editProposal(ctx.batchId, ctx.proposalId, {
            lines_to_add: next,
          });
          return;
        }

        // Existing flow: patch `lines_to_change` in the parent pre-order
        // proposal payload.
        const existing = (ctx.proposal.payload?.lines_to_change ?? []) as Array<
          Record<string, unknown>
        >;
        const idx = existing.findIndex((l) => l.id === flowId);
        const next =
          idx === -1
            ? [...existing, { id: flowId, ...payload }]
            : existing.map((l, i) => (i === idx ? { ...l, ...payload } : l));
        await editProposal(ctx.batchId, ctx.proposalId, {
          lines_to_change: next,
        });
      }
    },
    [ctx, operation, preOrder, editProposal],
  );

  const handleFlowCreate = useCallback(
    async (defaults: ProductDefaults): Promise<void> => {
      if (!ctx) return;
      const flowPayload = defaultsToFlowPayload(defaults);

      if (operation === "create") {
        // Adds a new pre_order_flow Proposal to the batch.
        await addFlowProposal(ctx.batchId, flowPayload);
        return;
      }

      if (operation === "update") {
        // Append to `lines_to_add` in the parent pre-order proposal payload.
        const existing = (ctx.proposal.payload?.lines_to_add ?? []) as Array<
          Record<string, unknown>
        >;
        await editProposal(ctx.batchId, ctx.proposalId, {
          lines_to_add: [...existing, flowPayload],
        });
      }
    },
    [ctx, operation, editProposal, addFlowProposal],
  );

  const handleFlowDelete = useCallback(
    async (flowId: string): Promise<void> => {
      if (!ctx) return;

      if (operation === "create") {
        const sourceProposalId = findFlowSourceProposalId(preOrder, flowId);
        if (!sourceProposalId) return;
        await removeProposal(ctx.batchId, sourceProposalId);
        return;
      }

      if (operation === "update") {
        // Virtual lines_to_add row: drop it from lines_to_add by index.
        const addIndex = parseLinesToAddIndex(flowId);
        if (addIndex !== null) {
          const existing = (ctx.proposal.payload?.lines_to_add ?? []) as Array<
            Record<string, unknown>
          >;
          if (addIndex >= existing.length) return;
          const next = existing.filter((_, i) => i !== addIndex);
          await editProposal(ctx.batchId, ctx.proposalId, {
            lines_to_add: next,
          });
          return;
        }

        // Existing flow: append to `lines_to_delete`.
        const existing = (ctx.proposal.payload?.lines_to_delete ??
          []) as Array<string>;
        if (existing.includes(flowId)) return;
        await editProposal(ctx.batchId, ctx.proposalId, {
          lines_to_delete: [...existing, flowId],
        });
      }
    },
    [ctx, operation, preOrder, editProposal, removeProposal],
  );

  return { handleFlowUpdate, handleFlowCreate, handleFlowDelete };
}
